import {
  Archive,
  BellOff,
  Bot,
  Calendar,
  Check,
  CheckCheck,
  Clock,
  EllipsisVertical,
  MailOpen,
  Paperclip,
  PhoneIncoming,
  PhoneOutgoing,
  Sparkles,
  Star,
  Trash2,
  UserPlus,
  UserRound,
  Voicemail,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/envelope';
import type { Conversation, Inbox, Message } from '../../api/types';
import { callDuration } from '../../lib/commsFormat';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ContactAvatar } from '../../components/ContactAvatar';
import { RefreshButton } from '../../components/RefreshButton';
import { useTimeFormat } from '../../lib/tenantTime';
import { channelKeyForInbox, CHANNEL_META, INBOX_ICONS, inboxIconKey } from '../../lib/commsMeta';
import { contactIdentityLine, identityLine } from '../../lib/channelMeta';
import { capabilitiesOf } from '../../lib/channelCapabilities';
import { useDismissOnOutside } from '../../lib/useDismiss';
import { useLoad } from '../../lib/useLoad';
import { CallButton } from './CallButton';
import { Composer, ComposerBotOwned, ComposerReadOnly } from './Composer';
import { composerDraftKey } from './drafts';
import { EmailBody, EmailRecipients } from './EmailBody';
import {
  ContactAttachment,
  EchoBadge,
  FlowResponseCard,
  LocationCard,
  MessageProblem,
  QuotedReply,
  ReferralNote,
  TemplateBadge,
} from './WhatsappParts';
import { waOf, waRendersBody } from '../../lib/whatsappMessage';
import { isMetaEcho } from '../../lib/metaMessage';
import { useComms } from './context';
import { AiBookingBox, type BookingSaved } from './booking/AiBookingBox';
import { bookerLinksFor } from './booking/bookerLinks';
import { BookingNoteText } from './booking/BookingNoteText';
import { ThreadBookings } from './booking/ThreadBookings';
import { ThreadReservation } from './booking/ThreadReservation';
import { useThreadBookings } from './booking/useThreadBookings';
import { ThreadSkeleton } from './Skeletons';
import { AssignMenuItems, MuteMenuItems, NotifyStrips, SnoozeMenuItems } from './ThreadControls';
import { assignTitle, snoozeUntil, useConversationMute, useThreadCallOptions } from './threadMenus';
import { hasDispatchBooker, mailSubjectOf } from './gmail/latestBookings';
import { useContactDetail } from './useContactDetail';
import { useModuleRefresh } from './useModuleRefresh';
import { useThread } from './useThread';
import { DispatchRoleChip } from './DispatchRoleChip';
import { LabelPicker } from './LabelPicker';
import { contactDisplayName, primaryIdentity } from './dispatchIdentity';
import { contactEmailOf, namesRecipients, sameSubject, subjectOf } from '../../lib/emailRecipients';
import { ourRef } from '../../lib/reservationRef';

function CallBubbleContent({ message }: { message: Message }) {
  const call = message.contentAttributes.call;
  const missed = call?.result === 'Missed' || call?.status === 'no_answer' || call?.status === 'busy';
  const voicemail = call?.result === 'Voicemail';
  const outbound = call?.direction?.toLowerCase() === 'outbound';
  const Icon = voicemail ? Voicemail : outbound ? PhoneOutgoing : PhoneIncoming;
  const tone = missed
    ? { bg: 'var(--c-red-muted)', fg: 'var(--c-red)' }
    : voicemail
      ? { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)' }
      : outbound
        ? { bg: 'var(--c-blue-muted)', fg: 'var(--c-blue)' }
        : { bg: 'var(--c-green-muted)', fg: 'var(--c-green)' };
  const label =
    message.content ??
    `${voicemail ? 'Voicemail' : missed ? 'Missed call' : outbound ? 'Outbound call' : 'Incoming call'}${
      call?.durationSeconds ? ` · ${callDuration(call.durationSeconds)}` : ''
    }`;
  return (
    <span className="msg-call">
      <span className="msg-call-icon" style={{ background: tone.bg, color: tone.fg }}>
        <Icon size={13} />
      </span>
      {label}
    </span>
  );
}

function Bubble({
  message,
  quoted,
  viaLabel,
  threadSubject,
  contactEmail,
  onRetry,
  onDraftBooking,
  onDelete,
}: {
  message: Message;
  /** The message this one replies to, when it is loaded in this thread. */
  quoted: Message | undefined;
  viaLabel: string;
  threadSubject: string;
  /** The contact's own address, which is where an outgoing reply went when it
   *  named no recipients itself. Null on a channel that has no addresses. */
  contactEmail: string | null;
  onRetry: (id: number) => void;
  /** Opens the AI booking box scoped to history up to this message; absent
   *  for send-denied agents, so no dead control renders. */
  onDraftBooking?: (id: number) => void;
  /** Delete this message, internally. Absent for read-only agents, same rule. */
  onDelete?: (id: number) => void;
}) {
  const { messageTime } = useTimeFormat();
  if (message.messageType === 'activity') {
    return <div className="msg-activity">{message.content}</div>;
  }
  const outgoing = message.messageType !== 'incoming';
  const bot = message.senderType === 'bot';
  const deleted = message.contentAttributes.deleted === true;
  const emailHtml = message.contentAttributes.email?.htmlContent?.sanitized;
  const email = !deleted && (message.contentType === 'incoming_email' || !!emailHtml);
  // Repeating the thread's own subject on every bubble is noise; a reply that
  // renamed the thread is exactly what an agent needs to see.
  // A forward carries its own title in `subject` rather than the inbound `email`
  // block, so a "Fwd: …" an agent sent shows as itself and not as the thread's.
  const subject = subjectOf(message);
  const ownSubject = subject && !sameSubject(subject, threadSubject) ? subject : '';
  // Inline images are already drawn inside the mail body; showing them again as
  // files would list the sender's logo and signature art as attachments. Only an
  // image can be drawn there, and the fileType check is what keeps a real file
  // visible: Outlook gives an ordinary attachment a Content-ID too, so a farm-in
  // reservation PDF arrives flagged inline and would vanish from the thread
  // entirely — nothing to open, and nothing to say it was ever sent.
  const attachments = email
    ? message.attachments.filter(
        (attachment) => attachment.meta?.inline !== true || attachment.fileType !== 'image',
      )
    : message.attachments;
  const failed = message.status === 'failed';
  const classes = [
    'msg',
    outgoing ? 'out' : 'in',
    bot ? 'bot' : '',
    message.private ? 'is-note' : '',
    failed ? 'failed' : '',
    deleted ? 'deleted' : '',
    email ? 'has-email' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const when = messageTime(message.externalCreatedAt ?? message.createdAt);
  const via = message.private ? 'Internal note' : bot ? 'Auto-reply · bot' : viaLabel;
  const wa = waOf(message);

  return (
    // The id is how the booking strip finds this bubble to scroll to it. A data
    // attribute rather than `id`, because two thread layouts exist (this one and
    // the Gmail one) and only one is ever mounted — but nothing here should
    // depend on that staying true.
    <div className={classes} data-msg-id={message.id}>
      <div className="msg-line">
        <div className="msg-bubble">
          {ownSubject && <div className="msg-email-subject">{ownSubject}</div>}
          {/* `email` is answered from the stored html, and a reply can be email
              without any — an attachment-only send, a plain-text one — so a
              message that NAMES its recipients shows them either way. */}
          {(email || namesRecipients(message)) && (
            <EmailRecipients message={message} fallbackTo={contactEmail} />
          )}
          <ReferralNote message={message} />
          {quoted && <QuotedReply quoted={quoted} />}
          {message.contentType === 'voice_call' ? (
            <CallBubbleContent message={message} />
          ) : email ? (
            <EmailBody message={message} />
          ) : waRendersBody(message) ? null : (
            // A booking's note carries its number as a link to the booking.
            <BookingNoteText message={message} />
          )}
          <LocationCard message={message} />
          <FlowResponseCard message={message} />
          <MessageProblem message={message} />
          {attachments.length > 0 && (
            <div className="msg-attachments">
              {attachments.map((attachment) =>
                attachment.fileType === 'contact' ? (
                  <ContactAttachment key={attachment.id} attachment={attachment} />
                ) : attachment.fileType === 'image' && attachment.dataUrl ? (
                  <a
                    key={attachment.id}
                    href={attachment.dataUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      className="msg-image"
                      src={attachment.dataUrl}
                      alt={attachment.fileName ?? 'attachment'}
                    />
                  </a>
                ) : attachment.fileType === 'audio' && attachment.dataUrl ? (
                  <audio key={attachment.id} className="msg-audio" src={attachment.dataUrl} controls>
                    <track kind="captions" />
                  </audio>
                ) : (
                  <a
                    key={attachment.id}
                    className="msg-attachment"
                    href={attachment.dataUrl ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Paperclip size={11} />
                    {attachment.fileName ?? `${attachment.fileType} attachment`}
                  </a>
                ),
              )}
            </div>
          )}
        </div>
        {/* Beside the message rather than in its meta line: what it drafts from
            is THIS message, and among the timestamps it read as one more badge.
            Still hover-only — see .msg-book in comms.css. */}
        {onDraftBooking && !outgoing && !message.private && !deleted && message.id > 0 && (
          <button
            type="button"
            className="msg-book"
            title="Draft a reservation from this message alone"
            onClick={() => onDraftBooking(message.id)}
          >
            <Sparkles size={11} />
            Draft reservation
          </button>
        )}
      </div>
      <div className="msg-meta">
        <span>{when}</span>
        <span>·</span>
        {/* An echo is the team's own reply typed on a phone — attributing it to this
            console would have an agent looking for a send they never made. */}
        {wa.echo ? (
          <EchoBadge />
        ) : isMetaEcho(message) ? (
          <EchoBadge where={message.contentAttributes.instagram ? 'Instagram app' : 'Page inbox'} />
        ) : (
          <span>{via}</span>
        )}
        <TemplateBadge message={message} />
        {/* Icon only, and only on hover (.msg-del in comms.css) — every bubble
            in a long thread carrying a visible Delete would make the thread
            about deleting. Already-deleted bubbles and optimistic ones (id < 0,
            not yet a row) have nothing to delete. */}
        {onDelete && !deleted && message.id > 0 && (
          <button
            type="button"
            className="msg-del"
            title="Delete this message"
            onClick={() => onDelete(message.id)}
          >
            <Trash2 size={11} />
          </button>
        )}
        {outgoing && !message.private && message.status === 'read' && <CheckCheck size={12} />}
        {outgoing && !message.private && (message.status === 'sent' || message.status === 'delivered') && (
          <Check size={12} style={{ color: 'var(--c-muted)' }} />
        )}
        {outgoing && message.status === 'pending' && (
          <Clock size={11} style={{ color: 'var(--c-muted)' }} />
        )}
        {failed && (
          <>
            <span className="msg-retry">
              {typeof message.contentAttributes.externalError === 'string'
                ? message.contentAttributes.externalError
                : 'Failed'}
            </span>
            {message.id > 0 && (
              <button type="button" className="msg-retry" onClick={() => onRetry(message.id)}>
                Retry
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** The open conversation: header actions, live message feed, composer. */
export function ThreadPane({
  conversation,
  inbox,
  onConversationPatched,
  onDeleted,
  onClosed,
  onToggleContext,
  contextOpen,
}: {
  conversation: Conversation;
  inbox: Inbox | undefined;
  /** The server confirmed a status/assignee change — parent refreshes rows. */
  onConversationPatched: () => void;
  /**
   * The thread was deleted. Separate from the patch above because the pane has
   * to CLOSE: the conversation it is rendering no longer answers any read, so
   * leaving it open would leave a thread whose every action now 404s.
   */
  onDeleted: () => void;
  /**
   * The thread is still there but this agent is done with it — today only
   * "mark as unread", which is undone the instant the pane stays open, since
   * opening a conversation is what reads it.
   */
  onClosed: () => void;
  onToggleContext: () => void;
  contextOpen: boolean;
}) {
  const {
    source,
    agents,
    canned,
    labels: labelCatalog,
    reloadLabels,
    templateBlocks,
    me,
    draftScope,
    sendDenied,
    noteSendDenied,
    canReply,
    replyBlocked,
  } = useComms();
  // Stable per source: the booking box keys its pick handler on it.
  const bookerLinks = useMemo(() => bookerLinksFor(source), [source]);
  // A thread the assistant handled opens whole: the handover note it left leans
  // on what was said long before the last page of messages.
  const thread = useThread(source, conversation.id, noteSendDenied, conversation.botHandled);
  const [menu, setMenu] = useState<'assign' | 'snooze' | 'mute' | 'more' | 'call' | null>(null);
  // One ref for all four: only the open menu is mounted, so it is the one holding it.
  const menuRef = useDismissOnOutside<HTMLDivElement>(menu !== null, () => setMenu(null));
  const [botBannerDismissed, setBotBannerDismissed] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  /** Which destructive thing is waiting on a confirm: the thread, or one message. */
  const [confirmDelete, setConfirmDelete] = useState<'conversation' | number | null>(null);
  const [deleting, setDeleting] = useState(false);
  // runId makes every Book-now click a fresh generation: the box is keyed on
  // it, so a re-click remounts rather than reviving stale state.
  const [booking, setBooking] = useState<{ fromMessageId?: number; runId: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const lastMessageId = useRef<number | null>(null);
  // What this thread has already produced in dispatch — the strip above the
  // scroll, kept current by `booking.created` so the second agent on an inbox
  // finds out before they book the same car.
  const threadBookings = useThreadBookings(source, conversation.id);

  const contact = conversation.contact;
  const detail = useContactDetail(source, contact.id);
  // The linked customer record wins: it is the identity dispatch VERIFIED, where
  // the phone match below only names the number. Both beat a contact whose
  // "name" is its own digits.
  const name = detail.data?.dispatchCustomer?.name || contactDisplayName(detail.data ?? contact);
  // Suppressed once the customer card names the same person — the panel beside
  // this header already spells the relationship out in full.
  const identity = detail.data?.dispatchCustomer ? null : primaryIdentity(detail.data ?? contact);
  // What they last booked, for the strip under the header. The detail read is
  // what LINKS a contact on first open, so its answer outranks the row's — and
  // keying the read on it re-asks once a link appears, rather than answering
  // "nothing" off a row from before the link existed. Both links key it: an
  // affiliate desk's bookings are this contact's too, and linking or forgetting
  // a desk must re-ask exactly as a customer link does.
  const links = detail.data ?? contact;
  const linkedBooker = hasDispatchBooker(links)
    ? `${links.dispatchCustomerId ?? ''}:${links.dispatchAffiliateId ?? ''}`
    : null;
  const latest = useLoad(
    () => (linkedBooker ? source.api.listLatestContactBookings([contact.id]) : Promise.resolve(null)),
    [source, contact.id, linkedBooker],
  );
  // A mail thread whose subject names a reservation shows that one instead.
  const subject = mailSubjectOf(conversation);
  const subjectRead = useLoad(
    () => (subject ? source.api.listSubjectBookings([conversation.id]) : Promise.resolve(null)),
    [source, conversation.id, subject],
  );
  const named = subjectRead.data?.payload.find((row) => row.conversationId === conversation.id) ?? null;
  const namedPending = !!subject && subjectRead.loading && !subjectRead.data;
  // A booking made from this thread is, by definition, now their latest: a
  // colleague's arrives as `booking.created` (the event the strip below the
  // header rides on too), and this agent's own save re-reads in recordBooking.
  const reloadLatest = latest.reload;
  useEffect(
    () =>
      source.subscribe((event) => {
        if (event.name !== 'booking.created') return;
        if ((event.payload as { conversationId?: number } | null)?.conversationId !== conversation.id) return;
        reloadLatest();
      }),
    [source, conversation.id, reloadLatest],
  );
  const meta = inbox ? CHANNEL_META[channelKeyForInbox(inbox)] : CHANNEL_META.all;
  const ChannelIcon = inbox ? INBOX_ICONS[inboxIconKey(inbox)] : meta.icon;
  const viaLabel = inbox?.name ?? meta.label;
  // Per THIS conversation's channel, not per role: an agent may be able to reply
  // in one thread and not the next.
  const replyDenied = sendDenied || (inbox ? !canReply(inbox.id) : false);
  const replyReason = inbox ? replyBlocked(inbox.id) : null;

  // Mail threads are titled: the subject is the one piece of an email an agent
  // scans for, and it lives on the conversation, not on any single message.
  const threadSubject = capabilitiesOf(inbox).subject
    ? (conversation.additionalAttributes.mailSubject ?? '').trim()
    : '';
  const contactEmail = contactEmailOf(conversation);

  /**
   * When the contact last wrote, which is what opens a WhatsApp reply window. Read off
   * the loaded thread rather than the conversation: waitingSince is cleared by our own
   * replies, so it says nothing about how long the window still has to run.
   */
  const lastInbound = useMemo(
    () =>
      [...thread.messages]
        .reverse()
        .find((message) => message.messageType === 'incoming' && !message.private),
    [thread.messages],
  );
  const lastInboundAt = lastInbound
    ? (lastInbound.externalCreatedAt ?? lastInbound.createdAt)
    : null;

  /**
   * Reply targets, by message id. `inReplyTo` is only set when the quoted message was
   * already ingested here, and it can still point above the loaded page — an unfound
   * id renders no quote rather than an empty one.
   */
  const byId = useMemo(
    () => new Map(thread.messages.map((message) => [message.id, message])),
    [thread.messages],
  );
  const quotedFor = (message: Message) => {
    const target = message.contentAttributes.inReplyTo;
    return typeof target === 'number' ? byId.get(target) : undefined;
  };

  // Follow the feed while the user is at the bottom; never yank them up-scroll.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newest = thread.messages[thread.messages.length - 1]?.id ?? null;
    if (newest !== lastMessageId.current && stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
    lastMessageId.current = newest;
  }, [thread.messages]);

  useEffect(() => {
    // Per-conversation UI state resets with the thread — deliberate sync reset.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBotBannerDismissed(false);
    setActionError(null);
    setBooking(null);
    stickToBottom.current = true;
  }, [conversation.id]);

  const runAction = async (action: () => Promise<unknown>) => {
    setMenu(null);
    setActionError(null);
    try {
      await action();
      onConversationPatched();
    } catch (e) {
      // Deliberately does NOT latch the read-only flag. Every 1036 here used to
      // mean "your role cannot send", so latching was a fair guess; now a refusal
      // can be specific to one action — assigning above your level, calling on a
      // channel you have no voice on — and latching would grey out the whole
      // console over it. Capability comes from GET /meta; the server's message
      // explains this particular refusal.
      setActionError(e instanceof ApiError ? e.message : 'That did not go through.');
    }
  };

  // The module-wide Refresh: messages and the contact behind the name. Not the
  // conversation row — InboxView reloads the list it comes from.
  useModuleRefresh(() => {
    detail.reload();
    if (linkedBooker) latest.reload();
    void thread.reload().catch(() => {});
  });

  /**
   * One click for the whole conversation: its messages, the row the header
   * reads from, and the contact card beside the name. Realtime normally keeps
   * all three current — this is the way back when it did not.
   */
  const refreshThread = () => {
    setMenu(null);
    setActionError(null);
    detail.reload();
    if (linkedBooker) latest.reload();
    onConversationPatched();
    void thread.reload().catch(() => setActionError('Could not refresh this conversation.'));
  };

  // Mute/unmute touch only the agent's own notification prefs — nothing on the
  // conversation changes, so no refetch.
  const runQuiet = async (action: () => Promise<unknown>) => {
    setMenu(null);
    setActionError(null);
    try {
      await action();
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'That did not go through.');
    }
  };

  /**
   * The CRM-side record of a dispatch write: one call that writes the row the
   * booking strip reads AND posts the private note beside it, against the
   * thread that was booked (captured in this closure), never merely the open
   * one — the agent may have moved on while dispatch was answering.
   *
   * The booking already exists when this runs, so nothing here may be retried
   * by retrying the CREATE: a failure is reported and the booking is left
   * alone. The note arrives over the socket rather than optimistically, because
   * the service is what writes it now — that is the price of the row and the
   * note never disagreeing about what this thread produced.
   */
  const recordBooking = async (saved: BookingSaved) => {
    const bookedDisplayId = conversation.id;
    setBooking(null);
    const kind = saved.kind === 'draft' ? 'draft' : saved.isQuote ? 'quote' : 'job';
    try {
      const { booking: row, notePosted } = await source.api.recordConversationBooking(
        bookedDisplayId,
        {
          kind,
          dispatchId: saved.dispatchId,
          bookerReference: saved.bookerReference,
          summary: saved.summary,
          pickupAt: saved.pickupAt,
          messageId: saved.messageId,
          trip: saved.trip,
        },
      );
      // The agent who just booked should not wait on their own socket to see
      // their card. Only when they are still on the thread it belongs to.
      if (bookedDisplayId === conversation.id) {
        threadBookings.add(row);
        if (linkedBooker) latest.reload();
      }
      if (!notePosted) {
        setActionError(`Created ${ourRef(kind, row.reference)}, but the note couldn't be posted.`);
      }
    } catch {
      setActionError(
        `Created ${ourRef(kind, saved.dispatchId)} in dispatch, but this thread couldn't record it.`,
      );
    }
  };

  /**
   * Show the message a booking was drafted from. Scrolls it into the middle of
   * the thread and rings it for a moment — a jump with no mark lands an agent
   * in the middle of a mail thread with nothing saying which bubble was meant.
   *
   * It only reaches messages already loaded. A booking made from something now
   * behind "Load earlier messages" scrolls nothing rather than jumping to the
   * wrong bubble, which is the failure worth avoiding here.
   */
  const showMessage = (messageId: number) => {
    const node = scrollRef.current?.querySelector(`[data-msg-id="${messageId}"]`);
    if (!node) {
      setActionError('That message is further back — load earlier messages to see it.');
      return;
    }
    setActionError(null);
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    node.classList.remove('is-located');
    // Reflow between the two, or removing and re-adding in one frame never
    // restarts the animation and a second click does nothing.
    void (node as HTMLElement).offsetWidth;
    node.classList.add('is-located');
  };

  // Mutes only — the same table stores 'watch' rows, and NotifyStrips owns those.
  const conversationMute = useConversationMute(conversation.id);

  const callOptions = useThreadCallOptions({
    contact,
    inbox,
    onRun: (action) => void runAction(action),
    onError: setActionError,
  });

  const botOwns = conversation.status === 'pending' && conversation.assigneeIsBot;
  /**
   * Stop the assistant and put this agent on the thread. The two halves are one
   * act — a conversation left 'open' with nobody on it is exactly the state the
   * assistant was covering.
   */
  const takeOverFromBot = () =>
    void runAction(async () => {
      await source.api.toggleStatus(conversation.id, 'open');
      if (me) await source.api.assignConversation(conversation.id, me.dashBoardId);
    });
  const showBotBanner = botOwns && !botBannerDismissed;

  /**
   * The star is ours and shared: nothing is told to the channel, and the whole
   * team sees it. The wanted state is sent rather than a flip, so two agents
   * pressing it at once agree instead of undoing each other.
   */
  const toggleStar = () =>
    void runAction(() => source.api.toggleStar(conversation.id, !conversation.starred));

  /**
   * The same shape one rung along: shared with the team, and on a Google
   * mailbox with the mailbox too. Not [runAction], which reports a refusal and
   * resolves — the picker has to hear about it, or the tick it painted
   * optimistically outlives the write the server turned down.
   */
  const applyLabels = async (next: string[]) => {
    setActionError(null);
    await source.api.setConversationLabels(conversation.id, next);
    // A title typed in the picker is a label that did not exist a moment ago.
    reloadLabels();
    onConversationPatched();
  };

  /**
   * Delete the thread, or one message in it. Both are internal — nothing is
   * unsent and no provider is told; a message already delivered is still on the
   * customer's phone, and this only decides what the team reads back.
   *
   * The two share a confirm because they share the sentence that matters: this
   * cannot be undone from here. The dialog closes on success either way; the
   * conversation case then closes the pane, because the thread it is rendering
   * no longer answers a read.
   */
  const runDelete = async () => {
    if (confirmDelete === null || deleting) return;
    setDeleting(true);
    setActionError(null);
    try {
      if (confirmDelete === 'conversation') {
        await source.api.deleteConversation(conversation.id);
        setConfirmDelete(null);
        onDeleted();
        return;
      }
      await thread.remove(confirmDelete);
      setConfirmDelete(null);
    } catch (e) {
      setConfirmDelete(null);
      setActionError(e instanceof ApiError ? e.message : 'That did not go through.');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * The header's left column is the SENDER. `conversation.contact` carries only
   * what the channel supplied, so the detail fetch is what turns it into a
   * person — dispatch's own name, their priority, the company they book under,
   * and the profile photo. It is one request per conversation open, shared with
   * the context panel (useContactDetail), and every field below degrades to the
   * channel identity while it is in flight.
   */
  const customer = detail.data?.dispatchCustomer ?? null;
  const priority = customer?.priority ?? null;
  const company = customer?.business?.name ?? null;
  // Their own address on this channel — not the inbox they wrote to.
  const senderIdentity = contactIdentityLine(contact, inbox?.channelType);

  return (
    // `thr-edge`: nothing is between this pane and the window's bottom-right
    // corner, where the help launcher floats — see .thr-edge in comms.css.
    <div className={contextOpen ? 'thr' : 'thr thr-edge'}>
      <div className="thr-head">
        {/* Sender on the left, the inbox they reached on the right. Which of the
            team's addresses a customer wrote to changes the reply — and it used
            to sit under the contact's name where it read as the CONTACT's
            address, which is the opposite of what it is. */}
        <ContactAvatar
          name={name}
          email={detail.data?.dispatchCustomer?.email ?? contact.email}
          photoUrl={detail.data?.avatarUrl}
          size={36}
        />
        <div className="thr-head-main">
          <div className="thr-head-toprow">
            <span className="thr-head-name" title={name}>
              {name}
            </span>
            {identity && <DispatchRoleChip role={identity.role} className="thr-head-role" />}
            {priority && Number(priority.code) > 1 && (
              <span
                className="thr-head-role"
                style={{ background: 'var(--c-orange-muted)', color: 'var(--c-orange)' }}
                title="Dispatch customer priority"
              >
                <Star size={9} style={{ marginRight: 3 }} />
                {priority.label}
              </span>
            )}
            {/* One chip and a count, never the whole set: this row also holds
                the name, and a thread with four labels would push it out of
                sight. The rest are a hover away here and a click away in the
                picker, which is where they are changed anyway. */}
            {conversation.labels[0] && (
              <span
                className="thr-head-role thr-head-label-chip"
                title={conversation.labels.join(', ')}
                style={{ background: 'var(--c-blue-muted)', color: 'var(--c-blue)' }}
              >
                {conversation.labels[0]}
                {conversation.labels.length > 1 && (
                  <span className="thr-head-label-more">+{conversation.labels.length - 1}</span>
                )}
              </span>
            )}
            {conversation.status === 'resolved' && (
              <span
                className="thr-head-role"
                style={{ background: 'var(--c-green-muted)', color: 'var(--c-green)' }}
              >
                Resolved
              </span>
            )}
            {/* Mail's subject, at the right end of the name row and so directly
                above the inbox it arrived at: the header then reads as two
                columns, who this is over their address, and what it is about
                over where it landed. It had a strip of its own below the
                header, which cost a border and two paddings to carry one
                line. */}
            {threadSubject && (
              <span className="thr-head-subject" title={threadSubject}>
                <span className="thr-head-subject-label">Subject:</span>
                <span className="thr-head-subject-text">{threadSubject}</span>
              </span>
            )}
          </div>
          <div className="thr-head-subrow">
            <span className="thr-head-from">
              {senderIdentity && (
                <span className="thr-head-who" title={senderIdentity}>
                  {senderIdentity}
                </span>
              )}
              {company && (
                <>
                  <span className="thr-head-dot">·</span>
                  <span className="thr-head-who" title={company}>
                    {company}
                  </span>
                </>
              )}
            </span>
            {/* One chip, not four parts. The channel is what its icon and its
                tint already say, so spelling it out beside the address only
                pushed the address — the thing that actually changes the reply —
                towards truncation. The word TO lives in the tooltip now. */}
            <span
              className="thr-head-to"
              style={{ background: meta.muted, color: meta.color }}
              title={inbox ? `Reached your ${meta.label} inbox — ${identityLine(inbox)}` : undefined}
            >
              <ChannelIcon size={11} />
              <span className="thr-head-box">{inbox ? identityLine(inbox) : meta.label}</span>
            </span>
          </div>
        </div>
        <div className="thr-head-actions">
          <CallButton
            options={callOptions}
            to={contact.phoneNumber ?? ''}
            open={menu === 'call'}
            onOpenChange={(next) => setMenu(next ? 'call' : null)}
          />
          <div className="thr-head-menu">
            <button
              type="button"
              className="cbtn cbtn-icon"
              // Named for what it does, whoever holds the thread: the holder is the
              // tooltip's to say (and the screen reader's description), since a name
              // that changed with every assignment is a control nobody can find by name.
              aria-label="Assign"
              title={assignTitle(conversation, agents, me?.dashBoardId)}
              onClick={() => setMenu(menu === 'assign' ? null : 'assign')}
            >
              <UserPlus size={16} />
            </button>
            {menu === 'assign' && (
              <div className="thr-menu" ref={menuRef}>
                <AssignMenuItems
                  conversation={conversation}
                  inbox={inbox}
                  onRun={(action) => void runAction(action)}
                />
              </div>
            )}
          </div>
          {/* Beside Snooze rather than inside the overflow menu: a flag is
              worth nothing if setting it costs two clicks, and its state has to
              be readable without opening anything. */}
          <button
            type="button"
            className="cbtn cbtn-icon"
            disabled={replyDenied}
            title={
              replyDenied
                ? 'You have read-only access to this channel'
                : conversation.starred
                  ? 'Remove star'
                  : 'Star this conversation'
            }
            style={
              conversation.starred
                ? { background: 'var(--c-orange-muted)', borderColor: 'var(--c-orange)' }
                : undefined
            }
            onClick={toggleStar}
          >
            <Star
              size={16}
              style={
                conversation.starred
                  ? { color: 'var(--c-orange)', fill: 'var(--c-orange)' }
                  : undefined
              }
            />
          </button>
          {/* Beside the star, because it is the same kind of thing: shared with
              the team, ours to set, and on a Google mailbox the mailbox's too.
              Keyed on the thread so the optimistic set inside cannot survive a
              move to another conversation. */}
          <LabelPicker
            key={conversation.id}
            applied={conversation.labels}
            catalog={labelCatalog.map((label) => label.title)}
            disabled={replyDenied}
            iconSize={16}
            onApply={applyLabels}
            onError={setActionError}
          />
          {/* Snooze and mute are one control: both answer "stop putting this in
              front of me", one by moving the row and one by silencing it, and
              two adjacent bells were the header's most confusable pair. */}
          <div className="thr-head-menu">
            <button
              type="button"
              className="cbtn cbtn-icon"
              title={
                conversationMute
                  ? 'Notifications muted for this conversation'
                  : 'Snooze or mute this conversation'
              }
              style={
                conversationMute || conversation.status === 'snoozed'
                  ? { background: 'var(--c-orange-muted)', borderColor: 'var(--c-orange)' }
                  : undefined
              }
              onClick={() => setMenu(menu === 'snooze' ? null : 'snooze')}
            >
              {conversationMute ? (
                <BellOff size={16} style={{ color: 'var(--c-orange)' }} />
              ) : (
                <Clock
                  size={16}
                  style={
                    conversation.status === 'snoozed' ? { color: 'var(--c-orange)' } : undefined
                  }
                />
              )}
            </button>
            {menu === 'snooze' && (
              <div className="thr-menu" ref={menuRef}>
                <SnoozeMenuItems
                  onSnooze={(hours) =>
                    void runAction(() =>
                      source.api.toggleStatus(conversation.id, 'snoozed', snoozeUntil(hours)),
                    )
                  }
                />
                <div className="thr-menu-sep" />
                <MuteMenuItems
                  conversationId={conversation.id}
                  mute={conversationMute}
                  onDone={(action) => void runQuiet(action)}
                />
              </div>
            )}
          </div>
          <RefreshButton
            className="cbtn cbtn-icon"
            size={16}
            label={null}
            title="Refresh this conversation"
            refreshing={thread.refreshing}
            busy={thread.loading}
            onClick={refreshThread}
          />
          {/* Worded rather than drawn: every other control here is an action on
              the thread, and an icon among them read as one more of those
              instead of the panel toggle it is. */}
          <button
            type="button"
            className="cbtn"
            title={contextOpen ? 'Hide contact details' : 'Show contact details'}
            style={
              contextOpen
                ? {
                    background: 'var(--c-primary-muted)',
                    borderColor: 'var(--c-primary)',
                    color: 'var(--c-primary)',
                  }
                : undefined
            }
            onClick={onToggleContext}
          >
            <UserRound size={16} />
            <span className="cbtn-label">Profile</span>
          </button>
          <div className="thr-head-menu">
            <button
              type="button"
              className="cbtn cbtn-icon"
              style={{ border: 'none', background: 'transparent' }}
              title="More"
              onClick={() => setMenu(menu === 'more' ? null : 'more')}
            >
              <EllipsisVertical size={19} style={{ color: 'var(--c-muted)' }} />
            </button>
            {menu === 'more' && (
              <div className="thr-menu" ref={menuRef}>
                {!replyDenied && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenu(null);
                      setBooking((prev) => ({ runId: (prev?.runId ?? 0) + 1 }));
                    }}
                  >
                    <Calendar size={13} />
                    Draft a reservation
                  </button>
                )}
                {conversation.status === 'resolved' ? (
                  <button
                    type="button"
                    onClick={() =>
                      void runAction(() => source.api.toggleStatus(conversation.id, 'open'))
                    }
                  >
                    <Archive size={13} />
                    Reopen conversation
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      void runAction(() => source.api.toggleStatus(conversation.id, 'resolved'))
                    }
                  >
                    <Archive size={13} />
                    Resolve conversation
                  </button>
                )}
                {/* The counterpart of opening it, which is what reads a
                    thread. It closes the pane rather than leaving it open —
                    an open thread that says it has not been read would be
                    read again by the next click on its row anyway. */}
                <button
                  type="button"
                  onClick={() => {
                    void runAction(async () => {
                      await source.api.markConversationUnread(conversation.id);
                      onClosed();
                    });
                  }}
                >
                  <MailOpen size={13} />
                  Mark as unread
                </button>
                {/* Last in the menu, and the only danger item in it. Hidden
                    rather than disabled for a read-only agent: they cannot
                    delete a message here either, so the row would only ever be
                    a dead one. */}
                {!replyDenied && (
                  <button
                    type="button"
                    className="thr-menu-danger"
                    onClick={() => {
                      setMenu(null);
                      setConfirmDelete('conversation');
                    }}
                  >
                    <Trash2 size={13} />
                    Delete conversation
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Under the header that says who they are: what they last booked. */}
      {(linkedBooker !== null || named !== null || namedPending) && (
        <ThreadReservation
          latest={latest}
          named={named}
          pending={namedPending}
          onOpenBookings={contextOpen ? undefined : onToggleContext}
        />
      )}

      {confirmDelete !== null && (
        <ConfirmDialog
          title={
            confirmDelete === 'conversation' ? 'Delete this conversation?' : 'Delete this message?'
          }
          body={
            confirmDelete === 'conversation' ? (
              <p>
                It leaves every list and every search for the whole team, and there is no way
                to bring it back from here. Nothing is sent to <b>{name}</b> and nothing is
                removed from their end — this only decides what your team reads back.
              </p>
            ) : (
              <p>
                The message is replaced by a note saying it was deleted, so the thread keeps its
                shape. If it already went out it is still on <b>{name}</b>&rsquo;s device — this
                only decides what your team reads back.
              </p>
            )
          }
          confirmLabel={confirmDelete === 'conversation' ? 'Delete conversation' : 'Delete message'}
          danger
          busy={deleting}
          onConfirm={() => void runDelete()}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {showBotBanner && (
        <div className="thr-bot-banner">
          <Bot size={16} />
          <span className="thr-bot-banner-text">
            <b>The bot is handling this conversation.</b> You can follow it here; take over to
            reply yourself.
          </span>
          <button type="button" className="thr-bot-takeover" onClick={takeOverFromBot}>
            Take over
          </button>
          <button type="button" className="cbtn cbtn-sm" onClick={() => setBotBannerDismissed(true)}>
            Keep bot on
          </button>
        </div>
      )}

      {/* Not on the hook for this thread, or following it anyway — see NotifyStrips. */}
      <NotifyStrips
        conversation={conversation}
        inbox={inbox}
        onRun={(action) => void runAction(action)}
      />

      <ThreadBookings
        bookings={threadBookings.bookings}
        onOpenMessage={showMessage}
      />

      {actionError && <div className="comms-error-note">{actionError}</div>}

      <div
        className="thr-scroll"
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        }}
      >
        {thread.hasOlder ? (
          <button
            type="button"
            className="thr-history-pill"
            onClick={thread.loadOlder}
            disabled={thread.loadingOlder}
          >
            {thread.loadingOlder && <span className="btn-spin" />}
            {thread.loadingOlder ? 'Loading earlier messages…' : 'Load earlier messages'}
          </button>
        ) : (
          thread.messages.length > 0 && <div className="thr-history-pill">Conversation history</div>
        )}
        {/* The header already switched to the new contact, so an empty body
            reads as "this thread has no messages" — hold its shape instead. */}
        {thread.loading && thread.messages.length === 0 && <ThreadSkeleton />}
        {thread.error && <div className="comms-error-note">{thread.error.message}</div>}
        {thread.messages.map((message) => (
          <Bubble
            key={message.id}
            message={message}
            quoted={quotedFor(message)}
            viaLabel={viaLabel}
            threadSubject={threadSubject}
            contactEmail={contactEmail}
            onRetry={(id) => void thread.retry(id)}
            onDraftBooking={
              replyDenied
                ? undefined
                : (id) =>
                    setBooking((prev) => ({ fromMessageId: id, runId: (prev?.runId ?? 0) + 1 }))
            }
            onDelete={replyDenied ? undefined : (id) => setConfirmDelete(id)}
          />
        ))}
      </div>

      {booking && !replyDenied && (
        <AiBookingBox
          key={`${conversation.id}:${booking.runId}`}
          conversation={conversation}
          fromMessageId={booking.fromMessageId}
          draftBooking={source.api.draftBooking}
          checkDuplicates={source.api.checkBookingDuplicates}
          bookerLinks={bookerLinks}
          dispatch={source.dispatch}
          onForbidden={noteSendDenied}
          onClose={() => setBooking(null)}
          onSaved={(saved) => void recordBooking(saved)}
        />
      )}

      {replyDenied ? (
        <ComposerReadOnly reason={replyReason} />
      ) : botOwns ? (
        // Permission first, then ownership: an agent who may not reply here is
        // told why, not offered a take-over that would refuse.
        <ComposerBotOwned onTakeOver={takeOverFromBot} />
      ) : (
        <Composer
          // One composer per thread. Its state IS that thread's half-written
          // reply, so an instance carried across a switch would offer what was
          // typed here for sending in the next conversation — and file it under
          // that conversation's draft.
          key={conversation.id}
          conversation={conversation}
          inbox={inbox}
          canned={canned}
          blocks={templateBlocks}
          lastInboundAt={lastInboundAt}
          lastInbound={lastInbound}
          threadSubject={threadSubject}
          draftKey={composerDraftKey(draftScope, conversation.id)}
          api={{
            getDraft: source.api.getDraft,
            saveDraft: source.api.saveDraft,
            draftReply: source.api.draftReply,
            presignUpload: source.api.presignUpload,
            putToS3: source.api.putToS3,
          }}
          onSend={thread.send}
          onSendAndClose={() =>
            runAction(() => source.api.toggleStatus(conversation.id, 'resolved'))
          }
        />
      )}
    </div>
  );
}
